import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { visibleCartCount } from '@theme/custom.cart-visible-count';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * Largest share of .cart-drawer__content the summary may claim and still be
   * pinned. At 0.5 the item list never gets less room than the summary it
   * competes with.
   * @type {number}
   */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * KEY-DECISION 2026-07-28: the ratio is re-measured, not measured once on open.
   * A stale 'true' collapses the item list to a sliver, because it is the only
   * flex child of .cart-drawer__content that can shrink.
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeObserver(() => this.#updateStickyState());

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartAddEvent.eventName, this.#remeasureAfterCartUpdate);
    // A restore re-renders the items without any open event, so the summary is re-measured here.
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#remeasureAfterCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#resetScrollPosition);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#clearAddedState);

    // A window resize or a device rotation changes the drawer box, and therefore
    // the ratio, without firing any of the events above.
    if (this.refs.dialog) this.#resizeObserver.observe(this.refs.dialog);

    if (history.state?.cartDrawerOpen) {
      // Clear only our own flag, keeping any other state on the entry (e.g. a saved-position id) so
      // unrelated features survive the drawer round-trip.
      const rest = { ...history.state };
      delete rest.cartDrawerOpen;
      history.replaceState(Object.keys(rest).length ? rest : null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartAddEvent.eventName, this.#remeasureAfterCartUpdate);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#remeasureAfterCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#resetScrollPosition);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#clearAddedState);
    this.#resizeObserver.disconnect();
    this.#historyAbortController?.abort();
  }

  /**
   * A drawer opens on a fresh view of the bag, and its newest line is the first one. The browser
   * restores scroll offsets inside it across a reload, so without this the shopper reopens the
   * bag part-way down the list with the scroll-hint's edge fade already on.
   *
   * Open only, never a cart mutation: an upsell toggle must keep the shopper's place, and
   * `showDialog()` returns early when the drawer is already open.
   */
  #resetScrollPosition = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    // Both boxes, not just the list: `cart-summary-sticky="false"` moves the overflow up to
    // .cart-drawer__content, so which one holds the scroll depends on the summary's height.
    for (const scroller of dialog?.querySelectorAll('.cart-drawer__content, .cart-drawer__items') ?? []) {
      scroller.scrollTop = 0;
    }
  };

  /**
   * The morph that swaps in the new summary runs in a sibling listener of this event, so wait a
   * frame before measuring.
   */
  #remeasureAfterCartUpdate = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      // A fresh position id (paginated-list.js's POSITION_ENTRY_KEY) keeps two drawer opens from
      // one listing distinct; the rest of the state is kept.
      const base = history.state && typeof history.state === 'object' ? history.state : {};
      const entryId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      history.pushState({ ...base, plpEntryId: entryId, cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Drops the post-add title back to the default. The drawer re-renders on every cart change, so the
   * state lives on the host, as `snippets/header-actions.liquid` says.
   */
  #clearAddedState = () => {
    this.removeAttribute('cart-added');
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean, source?: string, sections?: Record<string, string> } }>} event
   */
  #handleCartAdd = (event) => {
    // CartUpdateEvent shares this event name, so the source is what separates a real add from
    // a quantity edit or an upsell toggle - the latter two leave the title alone.
    // KEY-DECISION 2026-07-30: set outside the `auto-open` gate below - custom.customizer.js
    // opens the drawer itself whenever the merchant leaves that setting off.
    const { source, didError } = event.detail.data ?? {};
    if (source === 'product-form-component' && !didError) this.setAttribute('cart-added', 'true');

    // Upsell toggles and the customizer's chain and stone lines never auto-open: settling late,
    // they would reopen a drawer the shopper closed or queue behind another modal.
    if (
      this.hasAttribute('auto-open') &&
      source !== 'custom-cart-upsells' &&
      source !== 'custom-customizer-components'
    ) {
      // When the add originates inside an open MODAL <dialog> (e.g. quick-add),
      // defer the auto-open until that dialog's native `close` fires so its focus
      // restoration and scroll-lock teardown run first — opening immediately lets
      // the closing modal steal focus back and clobber the body scroll lock.
      // Non-modal dialogs don't close on add and don't move focus, so `:modal`
      // excludes them. Ported from upstream v4.0.0 (95ca657).
      const sourceModal = /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element ? event.target.closest('dialog:modal') : null
      );

      if (sourceModal?.open) {
        // Skip when the modal is this drawer (a quantity update) or the add failed: either way the
        // queued open would fire whenever the shopper later closes that dialog.
        if (!this.contains(sourceModal) && !event.detail.data?.didError) {
          sourceModal.addEventListener('close', () => this.showDialog(), { once: true });
        }
      } else {
        this.showDialog();
      }
    }

    // The section's visible count wins over `resource.item_count`, which counts hidden add-on
    // lines. Same call as cart-icon.js and header-actions.js, so the drawer matches the bubble.
    this.#announceCartCount(visibleCartCount(event.detail.data?.sections) ?? event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    // Reached through the cart icon, not an add, so start from the default title. The customizer's
    // forced open is safe: #handleCartAdd writes `cart-added` later, in the bubble phase.
    this.#clearAddedState();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    // A closed drawer has no box, so the ratio would divide by zero. The resize observer
    // fires once as soon as it starts observing, before the first open.
    if (!dialog?.open) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    let sticky;

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      sticky = 'false';
    } else {
      // KEY-DECISION 2026-07-28: measured against .cart-drawer__content, not the dialog: the
      // header and free-shipping bar sit outside the box the list and summary share.
      const contentHeight = content.getBoundingClientRect().height;
      if (!contentHeight) return;

      const summaryHeight = summary.getBoundingClientRect().height;
      sticky = summaryHeight / contentHeight > this.#summaryThreshold ? 'false' : 'true';
    }

    // Write only on a change: the attribute drives layout, and this runs on every resize frame.
    if (dialog.getAttribute('cart-summary-sticky') !== sticky) {
      dialog.setAttribute('cart-summary-sticky', sticky);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
